import { Extra, ExtraCheckout, ModalityIncludedExtra } from '../models/types';
import { calculateExtraTotal, getSelectedExtraAmount } from './extraPricing';
import { formatIncludedExtraName } from './includedExtra';
import { formatMeriendaIncludedDisplay, isMeriendaExtra } from './meriendaUpgrade';

export const VILLALBA_SHOP_ID = 2;
export const VILLALBA_NINO_EXTRA_ID = 13;
export const VILLALBA_MONITOR_EXTRA_ID = 4;
export const VILLALBA_MONITOR_PROMO_UNITS = 2;

export function isVillalbaShop(shopId: number, shopName?: string): boolean {
    if (Number(shopId) === VILLALBA_SHOP_ID) {
        return true;
    }
    return shopName?.toLowerCase().includes('villalba') ?? false;
}

export function isNinoExtra(extra: Pick<Extra, 'extra_id' | 'extra_name'>): boolean {
    if (Number(extra.extra_id) === VILLALBA_NINO_EXTRA_ID) {
        return true;
    }
    return extra.extra_name.toLowerCase().includes('niño extra');
}

export function isMonitorExtra(
    extra: Pick<Extra | ModalityIncludedExtra, 'extra_id' | 'extra_name'>
): boolean {
    if (Number(extra.extra_id) === VILLALBA_MONITOR_EXTRA_ID) {
        return true;
    }
    return /^monitor\b/i.test(extra.extra_name.trim());
}

function getNinoExtraUnits(extra: Extra): number {
    return getSelectedExtraAmount(extra);
}

export function qualifiesForVillalbaMonitorPromo(
    shopId: number,
    selectedExtras: Extra[],
    shopName?: string
): boolean {
    if (!isVillalbaShop(shopId, shopName)) {
        return false;
    }

    const ninoExtra = selectedExtras.find(isNinoExtra);
    if (!ninoExtra) {
        return false;
    }

    return getNinoExtraUnits(ninoExtra) >= 1;
}

export function formatIncludedExtraDisplay(
    extra: ModalityIncludedExtra,
    shopId: number,
    selectedExtras: Extra[],
    shopName?: string
): string {
    if (isMonitorExtra(extra) && qualifiesForVillalbaMonitorPromo(shopId, selectedExtras, shopName)) {
        return `Monitor (${VILLALBA_MONITOR_PROMO_UNITS} unidades)`;
    }

    if (isMeriendaExtra(extra)) {
        return formatMeriendaIncludedDisplay(extra, selectedExtras);
    }

    return formatIncludedExtraName(extra);
}

export function hasMonitorInIncludedExtras(includedExtras: ModalityIncludedExtra[]): boolean {
    return includedExtras.some(isMonitorExtra);
}

export interface VillalbaMonitorBonusLine {
    extra_id: number;
    extra_name: string;
    units: number;
}

export function getVillalbaStandaloneMonitorLine(
    shopId: number,
    selectedExtras: Extra[],
    includedExtras: ModalityIncludedExtra[],
    shopName?: string
): VillalbaMonitorBonusLine | null {
    if (!qualifiesForVillalbaMonitorPromo(shopId, selectedExtras, shopName)) {
        return null;
    }

    if (hasMonitorInIncludedExtras(includedExtras)) {
        return null;
    }

    return {
        extra_id: VILLALBA_MONITOR_EXTRA_ID,
        extra_name: 'Monitor',
        units: VILLALBA_MONITOR_PROMO_UNITS,
    };
}

export function formatIncludedExtraSelectionUnits(
    extra: ModalityIncludedExtra,
    shopId: number,
    selectedExtras: Extra[],
    shopName?: string
): string {
    if (isMonitorExtra(extra) && qualifiesForVillalbaMonitorPromo(shopId, selectedExtras, shopName)) {
        return `${VILLALBA_MONITOR_PROMO_UNITS} unidades`;
    }

    const units = Number(extra.included_units);
    if (units > 1) {
        return `Hasta ${units} unidades`;
    }

    return '';
}

export function formatMonitorBonusUnitsLabel(bonus: VillalbaMonitorBonusLine): string {
    return bonus.units > 1 ? `${bonus.units} unidades` : '';
}

export function formatMonitorBonusLine(bonus: VillalbaMonitorBonusLine): string {
    return bonus.units > 1
        ? `${bonus.extra_name} (${bonus.units} unidades)`
        : bonus.extra_name;
}

export function buildCheckoutExtras(
    shopId: number,
    selectedExtras: Extra[],
    shopName?: string
): ExtraCheckout[] {
    const checkoutExtras: ExtraCheckout[] = selectedExtras.map((extra) => ({
        extra_id: extra.extra_id,
        extra_amount: getSelectedExtraAmount(extra),
        extra_total_price: calculateExtraTotal(extra),
    }));

    if (!qualifiesForVillalbaMonitorPromo(shopId, selectedExtras, shopName)) {
        return checkoutExtras;
    }

    const monitorAlreadySelected = selectedExtras.some(isMonitorExtra);
    if (monitorAlreadySelected) {
        return checkoutExtras;
    }

    checkoutExtras.push({
        extra_id: VILLALBA_MONITOR_EXTRA_ID,
        extra_amount: 1,
        extra_total_price: 0,
    });

    return checkoutExtras;
}
